import router from '@/core/router';

/**
 * Redirect to the local login page.
 * Preserves the current URL as redirect_uri so the user returns after login.
 */
// Single-flight guard: when a query keeps failing (e.g. stale session cookie),
// the Apollo error link fires for every retry while the router transition to
// /login is still in flight. Without this latch each call would queue a fresh
// `router.replace`, the new nav re-runs the auth middleware, which re-fires
// fetchMe, which fails, which re-enters this function — an infinite loop.
let loginRedirectInFlight = false;

export const goToLogin = (): void => {
  if (loginRedirectInFlight || window.location.pathname === '/login' || router.currentRoute.value.name === 'Login') {
    return;
  }

  loginRedirectInFlight = true;

  const currentPath = window.location.pathname + window.location.search;

  router.replace({
    name: 'Login',
    query: { redirect_uri: currentPath },
  }).finally(() => {
    loginRedirectInFlight = false;
  });
};

/**
 * @deprecated Alias for goToLogin — kept for backward compatibility.
 */
export const goToSSO = goToLogin;

/**
 * Build the logout URL for the API's REST logout endpoint.
 */
export const getLogoutUrl = (): string => {
  const apiUrl = import.meta.env.VITE_DOTT_API_URL;

  return `${apiUrl}/auth/logout`;
};

/**
 * The magic-link round-trip bounces the user out to email and back via
 * `/magic-link`, which loses the `?redirect_uri=…` query that the
 * original `/login` URL was carrying. We stash the intended destination
 * in a short-lived client cookie at email-submit time so `MagicLinkPage`
 * (which has no access to the original query) can route the user back
 * to where they were trying to go after a successful consume.
 *
 * TTL must cover the longest full auth path: the 7-day welcome link
 * in `magic-link.constants.ts` plus the 2FA pending-token window in
 * `jwt.config.ts` (`twoFaPendingToken: '10m'`). A user can
 * click the welcome link at the very end of its 7-day window and then
 * still have 10 more minutes to complete OTP — if the cookie expired
 * at exactly 7 days, the redirect would be lost on the 2FA path even
 * though sign-in itself succeeds. We add a small buffer beyond that
 * window to absorb clock skew between client/server.
 *
 * The cookie is safe to keep for the full window because every fresh
 * `/login` visit either overwrites it with the new query value or
 * clears it via `stashAuthRedirect(null)` on email submit, and every
 * successful sign-in (`completeLogin` in `LoginPage`, `consumeRedirectTarget`
 * in `MagicLinkPage`) clears it explicitly — so a stale value cannot
 * bleed into an unrelated subsequent sign-in.
 */
const REDIRECT_COOKIE_NAME = 'auth_redirect_uri';
const REDIRECT_COOKIE_MAX_AGE_S = 7 * 24 * 60 * 60 + 15 * 60;

/**
 * Stash the redirect target for the magic-link round-trip. Pass `null`
 * (or an empty string) to clear — used on plain `/login` visits to
 * avoid a stale cookie from a prior session bleeding into a fresh one.
 *
 * Validates that the value is a same-origin relative path so a tampered
 * cookie can't be used for an open redirect after auth.
 *
 * Accepts the raw shape Vue Router exposes for `route.query.*` —
 * `string | (string | null)[] | null | undefined` — and treats anything
 * other than a single string (repeated `?redirect_uri=…&redirect_uri=…`
 * query params, `null`, an empty array, etc.) as a clear. Without that
 * guard, a user-controlled login URL with duplicate `redirect_uri`
 * params would crash this function on `value.startsWith(...)` and
 * break the submit handler before the mutation fires.
 */
export const stashAuthRedirect = (
  value: string | (string | null)[] | null | undefined,
): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  const single = typeof value === 'string' ? value : null;

  if (!single || !single.startsWith('/') || single.startsWith('//')) {
    document.cookie = `${REDIRECT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${secure}`;

    return;
  }

  document.cookie
    = `${REDIRECT_COOKIE_NAME}=${encodeURIComponent(single)}; path=/; max-age=${REDIRECT_COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
};

/**
 * Read the stashed redirect target. Returns `null` when the cookie is
 * absent, malformed, or fails the same-origin-relative-path check —
 * caller should fall back to `/`.
 */
export const readAuthRedirect = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)auth_redirect_uri=([^;]+)/);
  const raw = match?.[1];

  if (!raw) {
    return null;
  }

  try {
    const value = decodeURIComponent(raw);

    if (!value.startsWith('/') || value.startsWith('//')) {
      return null;
    }

    return value;
  } catch {
    return null;
  }
};

/**
 * Clear the stashed redirect target. Call after the cookie has been
 * consumed so a subsequent unrelated sign-in doesn't pick up a stale
 * destination.
 */
export const clearAuthRedirect = (): void => {
  stashAuthRedirect(null);
};
